import { useState } from "react";
import { useNavigate, useRevalidator } from "react-router-dom";
import { PriceInput } from "./PriceInput";
import { SquareArrowOutUpRight } from "lucide-react";
import Spinner from "../spinners/Spinner";
import { MEDICAL_ICONS } from "./medical_icons";
import { getToken } from "../../utils/token";
import instance from "../../axios/instance";
import { toast } from "react-toastify";

export const TreatmentEditor = ({ treatment, onClose, editing }) => {
  const [formData, setFormData] = useState(treatment);
  const [loading, setLoading] = useState(false);
  const [image, setImage] = useState(null);
  const revalidator = useRevalidator();
  const { description_list, payment_type, description, subtitle, title } =
    formData;

  const navigate = useNavigate();
  const descriptionWords = description.trim().split(" ");
  const buttonNotActive =
    description_list.length < 1 ||
    !payment_type ||
    !description ||
    descriptionWords.length < 24 ||
    descriptionWords.length > 30 ||
    !subtitle ||
    !title;

  const handleDescriptionItemChange = (index, field, value) => {
    const newItems = [...formData.description_list];
    newItems[index] = { ...newItems[index], [field]: value };
    setFormData({ ...formData, description_list: newItems });
  };

  const addDescriptionItem = () => {
    if (formData.description_list.length < 4) {
      setFormData({
        ...formData,
        description_list: [
          ...formData.description_list,
          { text: "", icon: "Heart" },
        ],
      });
    }
  };

  const removeDescriptionItem = (index) => {
    const newItems = formData.description_list.filter((_, i) => i !== index);
    setFormData({ ...formData, description_list: newItems });
  };

  const validateDescription = (text) => {
    const wordCount = text.trim().split(/\s+/).length;
    return wordCount >= 24 && wordCount <= 30;
  };

  const handleSubmit = async () => {
    const newFormData = new FormData();
    newFormData.append("formData", JSON.stringify(formData));
    newFormData.append("file", image);
    try {
      const token = getToken();
      setLoading(true);
      if (editing) {
        await instance.put(
          `/doctor/clinic/treatments/edit/${treatment.id}`,
          newFormData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        toast.success("Treatment Updated");
        // force a reload if image is changed
        if (image) {
          window.location.reload();
        } else {
          revalidator.revalidate();
        }
        onClose();
      } else {
        await instance.post(`/doctor/clinic/treatments/new`, newFormData, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        toast.success("New Treatment Created");
        navigate("/dashboard/settings/treatment-setup/treatments");
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
      toast.error(
        error.response ? error.response.data.message : "An error occurred"
      );
    }
  };

  const handleFileInput = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 10000000) {
      setError("File size should be less than 10MB");
      return;
    }

    setImage(file);
  };

  return (
    <div className="fixed top-0 right-0 w-[600px] h-[100vh] z-50 overflow-y-scroll scrollbar-hide bg-white p-4">
      <div className="p-6">
        <h2 className="text-2xl font-bold mb-6">Edit Treatment Details</h2>

        <div className="space-y-6">
          {/* Title Input */}
          <div className="space-y-2">
            <label
              htmlFor="title"
              className="block text-sm font-medium text-gray-700"
            >
              Title
            </label>
            <input
              id="title"
              type="text"
              value={formData.title}
              onChange={(e) =>
                setFormData({ ...formData, title: e.target.value })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary"
            />
          </div>
          <div className="space-y-2">
            <label
              htmlFor="subtitle"
              className="block text-sm font-medium text-gray-700"
            >
              Subtitle
            </label>
            <input
              id="subtitle"
              type="text"
              value={formData.subtitle}
              onChange={(e) =>
                setFormData({ ...formData, subtitle: e.target.value })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary"
            />
          </div>

          {/* Description Items */}
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium text-gray-700">
                Description Items
              </span>
              {formData.description_list.length < 4 && (
                <button
                  onClick={addDescriptionItem}
                  className="px-3 py-1 text-sm bg-primary text-white rounded-md hover:bg-teal-800 transition-colors"
                >
                  Add Item
                </button>
              )}
            </div>

            {formData.description_list.map((item, index) => (
              <div key={index} className="space-y-2 p-4 border rounded-lg">
                <div className="flex justify-between">
                  <span className="text-sm font-medium text-gray-700">
                    Item {index + 1}
                  </span>
                  {formData.description_list.length > 1 && (
                    <button
                      onClick={() => removeDescriptionItem(index)}
                      className="px-3 py-1 text-sm bg-red-500 text-white rounded-md hover:bg-red-600 transition-colors"
                    >
                      Remove
                    </button>
                  )}
                </div>

                <input
                  type="text"
                  value={item.text}
                  onChange={(e) =>
                    handleDescriptionItemChange(index, "text", e.target.value)
                  }
                  placeholder="Description item text"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary"
                />

                <div className="grid grid-cols-5 gap-2 mt-2">
                  {MEDICAL_ICONS.map(({ icon: Icon, name }) => (
                    <button
                      key={name}
                      onClick={() =>
                        handleDescriptionItemChange(index, "icon", name)
                      }
                      className={`p-2 border rounded-lg flex items-center justify-center ${
                        item.icon === name
                          ? "border-primary bg-primary-light"
                          : "hover:bg-gray-100"
                      }`}
                    >
                      <Icon className="w-6 h-6" />
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Main Description */}
          <div className="space-y-2">
            <label
              htmlFor="description"
              className="block text-sm font-medium text-gray-700"
            >
              Description (24-30 words)
            </label>
            <textarea
              id="description"
              value={formData.description}
              onChange={(e) => {
                const newText = e.target.value;
                setFormData({ ...formData, description: newText });
              }}
              className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary ${
                !validateDescription(formData.description)
                  ? "border-red-500"
                  : "border-gray-300"
              }`}
              rows={4}
            />
            <div className="text-sm text-gray-500">
              Word count: {formData.description.trim().split(/\s+/).length}
            </div>
          </div>

          {/* Price Input */}
          <PriceInput paymentData={formData} setPaymentData={setFormData} />

          <div className="space-y-2 grid grid-cols-2">
            <label className="flex items-center">
              <input
                type="radio"
                value={formData.instock}
                checked={formData.instock === "true"}
                onChange={(e) => {
                  setFormData({ ...formData, instock: "true" });
                }}
                className="mr-2"
              />
              <span>In Stock</span>
            </label>
            <label className="flex items-center">
              <input
                type="radio"
                value={formData.instock}
                checked={formData.instock !== "true"}
                onChange={(e) => {
                  setFormData({ ...formData, instock: "false" });
                }}
                className="mr-2"
              />
              <span>Out of Stock</span>
            </label>
          </div>
          <div className="space-y-2">
            <label
              htmlFor="image"
              className="block text-sm font-medium text-gray-700"
            >
              Treatment Image
            </label>
            <input
              id="image"
              type="file"
              accept="image/*"
              onChange={handleFileInput}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-primary hover:file:bg-blue-100"
            />
            {editing && (
              <a
                href={treatment.image_url}
                target="_blank"
                className="text-xs flex items-center gap-x-2 mt-1 text-primary"
              >
                Current Treatment Image
                <SquareArrowOutUpRight size={15} />
              </a>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end gap-4 pt-4">
            <button
              onClick={() => onClose()}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              className="flex items-center justify-center min-w-[150px] px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:opacity-70 transition duration-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-60 disabled:cursor-not-allowed"
              disabled={buttonNotActive}
            >
              {loading ? <Spinner color={"white"} /> : "Save Changes"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
